/**
 * BlockCountdownOverlay
 *
 * Floating overlay that appears over the video when a session block is about to end.
 * Shows a 15-second countdown with the next block/person info.
 * Reads blocks from Firestore via useStructuredGrowth, synced via useSessionFlow.
 */

import React, { useState, useRef, useMemo, memo } from 'react';
import { useStructuredGrowth } from './useStructuredGrowth';
import { useSessionFlow } from './useSessionFlow';
import type { SessionBlock, SessionBlockType, StructuredGrowthEntityType } from './types';

const COUNTDOWN_SECONDS = 15;

const DEFAULT_BLOCKS: SessionBlock[] = [
  { id: 'b1', type: 'intro', label: 'Introductie', durationMinutes: 5, order: 0 },
  { id: 'b2', type: 'hotseat', label: 'Leden ronde 1', durationMinutes: 9, order: 1, perPerson: true, memberSlotStart: 0, memberSlotCount: 3 },
  { id: 'b3', type: 'custom', label: 'Pauze', durationMinutes: 5, order: 2 },
  { id: 'b4', type: 'hotseat', label: 'Leden ronde 2', durationMinutes: 9, order: 3, perPerson: true, memberSlotStart: 3, memberSlotCount: 2 },
  { id: 'b5', type: 'commitment', label: 'Afsluiting', durationMinutes: 5, order: 4 },
];

const TYPE_COLORS: Record<SessionBlockType, string> = {
  intro: '#60A5FA',
  reflection: '#A78BFA',
  hotseat: '#FBBF24',
  commitment: '#34D399',
  custom: '#9CA3AF',
};

interface BlockCountdownOverlayProps {
  entityId: string;
  entityType?: StructuredGrowthEntityType;
  meetingId: string;
  memberCount?: number;
  memberNames?: string[];
  memberAvatars?: (string | null)[];
}

const BlockCountdownOverlay: React.FC<BlockCountdownOverlayProps> = memo(function BlockCountdownOverlay({
  entityId,
  entityType = 'mastermind',
  meetingId,
  memberCount = 1,
  memberNames = [],
  memberAvatars = [],
}) {
  const { settings } = useStructuredGrowth(entityId, entityType);
  const blocks = (settings?.sessionStructure ?? []).length > 0
    ? settings!.sessionStructure
    : DEFAULT_BLOCKS;

  const {
    currentBlock,
    remainingSeconds,
    flow,
  } = useSessionFlow(entityId, meetingId, blocks, { canControl: false });

  const [currentPersonIdx, setCurrentPersonIdx] = useState(0);
  const prevBlockRef = useRef(flow.currentBlockIndex);
  if (prevBlockRef.current !== flow.currentBlockIndex) {
    prevBlockRef.current = flow.currentBlockIndex;
    setCurrentPersonIdx(0);
  }

  const sortedBlocks = useMemo(() => [...blocks].sort((a, b) => a.order - b.order), [blocks]);

  if (!currentBlock || !flow.isRunning) return null;

  const effectiveMembers = Math.max(1, memberCount);
  const slotStart = currentBlock.memberSlotStart ?? 0;
  const slotCount = currentBlock.memberSlotCount ?? effectiveMembers;
  const isPerPerson = currentBlock.perPerson && slotCount > 0;

  const showCountdown = remainingSeconds <= COUNTDOWN_SECONDS && remainingSeconds > 0;
  if (!showCountdown) return null;

  let nextLabel = '';
  let nextAvatar: string | null = null;
  let nextColor = '#34D399';

  if (isPerPerson && currentPersonIdx < slotCount - 1) {
    const nextGlobalIdx = slotStart + currentPersonIdx + 1;
    nextLabel = memberNames[nextGlobalIdx] ?? `Persoon ${nextGlobalIdx + 1}`;
    nextAvatar = memberAvatars[nextGlobalIdx] ?? null;
    nextColor = TYPE_COLORS[currentBlock.type];
  } else if (flow.currentBlockIndex < sortedBlocks.length - 1) {
    const nextBlock = sortedBlocks[flow.currentBlockIndex + 1];
    if (nextBlock.perPerson) {
      const nSlotStart = nextBlock.memberSlotStart ?? 0;
      nextLabel = memberNames[nSlotStart] ?? `Persoon ${nSlotStart + 1}`;
      nextAvatar = memberAvatars[nSlotStart] ?? null;
    } else {
      nextLabel = nextBlock.label;
    }
    nextColor = TYPE_COLORS[nextBlock.type];
  } else {
    nextLabel = 'Einde meeting';
    nextColor = '#EF4444';
  }

  const circleProgress = ((COUNTDOWN_SECONDS - remainingSeconds) / COUNTDOWN_SECONDS) * 100;
  const isUrgent = remainingSeconds <= 5;
  const isWarning = remainingSeconds <= 10 && !isUrgent;

  return (
    <div className="absolute left-4 top-1/2 -translate-y-1/2 z-[60] pointer-events-none" style={{ animation: 'bcoFadeIn 0.35s cubic-bezier(0.16, 1, 0.3, 1) both' }}>
      <div className="bg-black/80 backdrop-blur-md rounded-2xl p-4 shadow-2xl border border-white/10 min-w-[130px]">
        {/* Countdown circle */}
        <div className="relative w-16 h-16 mx-auto mb-2.5">
          <svg className="w-full h-full -rotate-90" viewBox="0 0 64 64">
            <circle cx="32" cy="32" r="28" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="3" />
            <circle
              cx="32" cy="32" r="28" fill="none"
              stroke={isUrgent ? '#EF4444' : isWarning ? '#FBBF24' : nextColor}
              strokeWidth="3"
              strokeLinecap="round"
              strokeDasharray={`${Math.PI * 56}`}
              strokeDashoffset={`${Math.PI * 56 * (1 - circleProgress / 100)}`}
              style={{ transition: 'stroke-dashoffset 1s linear, stroke 0.3s' }}
            />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <span className={`text-2xl font-mono font-black tabular-nums ${
              isUrgent ? 'text-red-400' : isWarning ? 'text-amber-400' : 'text-white'
            }`} style={isUrgent ? { animation: 'bcoPulse 0.5s ease-in-out infinite' } : undefined}>
              {remainingSeconds}
            </span>
          </div>
        </div>

        {/* "Volgende" label */}
        <p className="text-[8px] uppercase tracking-[0.15em] text-white/35 font-bold text-center mb-1.5">
          Volgende
        </p>

        {/* Next person/block info */}
        <div className="flex flex-col items-center gap-1.5">
          {nextAvatar ? (
            <img src={nextAvatar} alt="" className="w-8 h-8 rounded-full object-cover ring-2 ring-offset-1 ring-offset-transparent" style={{ borderColor: nextColor }} />
          ) : (
            <div className="w-8 h-8 rounded-full flex items-center justify-center text-[11px] font-bold text-white/80" style={{ backgroundColor: nextColor + '30', border: `2px solid ${nextColor}` }}>
              {nextLabel.charAt(0)}
            </div>
          )}
          <span className="text-[11px] font-semibold text-white truncate max-w-[100px] text-center leading-tight">{nextLabel}</span>
        </div>
      </div>

      <style>{`
        @keyframes bcoFadeIn {
          from { opacity: 0; transform: translateY(-50%) translateX(-20px) scale(0.9); }
          to   { opacity: 1; transform: translateY(-50%) translateX(0) scale(1); }
        }
        @keyframes bcoPulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.15); }
        }
      `}</style>
    </div>
  );
});

export default BlockCountdownOverlay;
